/**
 * MCCGA VIP Email Pop-up — behaviour + submit handler
 * =====================================================================
 *
 * SHOW-ONCE LOGIC
 * ---------------
 * localStorage key: "mccga_popup_v1"
 * Values: undefined (never seen) → "dismissed" (closed X) → "signed_up"
 * The popup never re-opens once either flag is set.
 *
 * TRIGGER
 * -------
 * Fires after `data-delay` ms (set via section settings, default 3 000 ms)
 * OR after the user scrolls 25 % of the page — whichever comes first.
 * Both triggers cancel each other once the popup fires.
 *
 * =====================================================================
 * INTEGRATION POINT — wire to your list provider
 * =====================================================================
 *
 * OPTION A — Klaviyo client-side subscribe  ★ RECOMMENDED ★
 * ----------------------------------------------------------
 * Uses Klaviyo's /client/subscriptions/ endpoint.
 * Requires only a PUBLIC key + list ID — both are safe for client-side
 * use; they are NOT secret. Anyone can see them in the page source and
 * that is fine by design (they only allow subscribing, not reading).
 *
 * How to configure (no code edit needed):
 *   Online Store → Themes → Customize → "VIP Pop-up" section →
 *   paste Klaviyo Public Key + List ID.
 *
 * Where to find them:
 *   Public key : Klaviyo → Account → Settings → API Keys → "Public API Key"
 *   List ID    : Klaviyo → Lists & Segments → [your list] → ID in the URL
 *                e.g. https://www.klaviyo.com/list/RaTxyz → "RaTxyz"
 *
 * This option is active by default. If the section settings are empty,
 * the JS falls back to OPTION C (Shopify contact form) with a warning.
 *
 * ----------------------------------------------------------
 * OPTION B — Shopify Customer API (server-side only)
 * ----------------------------------------------------------
 * Requires write_customers OAuth scope — cannot be called safely from
 * a browser without exposing an admin token. Use only if you have a
 * Shopify Function or a small proxy (e.g. Cloudflare Worker) that
 * holds the token server-side and forwards the request.
 * Not implemented here. See:
 * https://shopify.dev/docs/api/admin-rest/latest/resources/customer#post-customers
 *
 * ----------------------------------------------------------
 * OPTION C — native {% form 'customer' %} submit (default fallback)
 * ----------------------------------------------------------
 * Zero config; the section wraps the fields in {% form 'customer' %},
 * so the browser's normal POST to /contact carries Shopify's CSRF
 * token and creates a Customer tagged "newsletter,vip-popup" (+ a
 * wn:<handle> tag when a Whatnot username is given). Success/error
 * comes back via a page reload + Liquid markers (#mccga-posted /
 * #mccga-form-errors). Subscribers appear in Shopify → Customers but
 * DO NOT flow into Klaviyo automatically.
 * NOTE: an AJAX fetch("/contact") does NOT work — Shopify rejects
 * tokenless posts with HTTP 400 (verified live 2026-07-06).
 * Activated automatically when no Klaviyo key/list is set.
 *
 * =====================================================================
 */

(() => {
	if (window.mccgaSalePopupActive?.()) return;
	var STORAGE_KEY = "mccga_popup_v1";

	/* ── Native-submit round-trip state (rendered by Liquid) ──────────── */
	/* After the native {% form 'customer' %} POST, Shopify reloads this page;
	   the section renders #mccga-posted on success or #mccga-form-errors on a
	   validation error. Both must be handled BEFORE the show-once gate. */
	var postedMarker = document.getElementById("mccga-posted");
	var errorsMarker = document.getElementById("mccga-form-errors");

	/* ── Bail early if already signed-up or dismissed ─────────────────── */
	/* On preview/unpublished themes, skip the once-per-visitor gate so the
	   pop-up can be reviewed on every load; the live theme still shows once. */
	var isLiveTheme =
		!(window.Shopify && Shopify.theme) || Shopify.theme.role === "main";
	try {
		var stored = localStorage.getItem(STORAGE_KEY);
		if (
			!postedMarker &&
			!errorsMarker &&
			isLiveTheme &&
			(stored === "signed_up" || stored === "dismissed")
		)
			return;
	} catch (e) {
		/* localStorage blocked (private browsing + Safari ITP) — still show */
	}

	/* ── Grab DOM refs ────────────────────────────────────────────────── */
	var dialog = document.getElementById("mccga-popup");
	var form = document.getElementById("mccga-popup-form");
	var closeBtn = document.getElementById("mccga-popup-close");
	var dismissBtn = document.getElementById("mccga-popup-dismiss");
	var emailIn = document.getElementById("mccga-email");
	var emailErr = document.getElementById("mccga-email-err");
	var submitBtn = document.getElementById("mccga-popup-submit");
	var successEl = document.getElementById("mccga-popup-success");

	if (!dialog || !form) return; /* snippet not present on this page */

	/* ── Read integration config from data attributes ─────────────────── */
	var KLAVIYO_KEY = (dialog.dataset.klaviyoKey || "").trim();
	var KLAVIYO_LIST = (dialog.dataset.klaviyoList || "").trim();
	var delay = parseInt(dialog.dataset.delay, 10) || 3000;

	/* ── Open helper ──────────────────────────────────────────────────── */
	function openPopup() {
		try {
			dialog.showModal();
		} catch (e) {
			/* <dialog> not supported — polyfill or skip */
			dialog.setAttribute("open", "");
		}
		/* Move focus inside for keyboard users */
		var firstFocusable = dialog.querySelector(
			'button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		if (firstFocusable) firstFocusable.focus();
	}

	/* ── Close helper ─────────────────────────────────────────────────── */
	function closePopup(reason) {
		try {
			dialog.close();
		} catch (e) {
			dialog.removeAttribute("open");
		}
		flag(reason || "dismissed");
		cancelTriggers();
	}

	/* ── Persist flag ─────────────────────────────────────────────────── */
	function flag(value) {
		try {
			localStorage.setItem(STORAGE_KEY, value);
		} catch (e) {}
	}

	/* ── Trigger logic (delay + scroll, whichever fires first) ────────── */
	var fired = false;
	var timerHandle = null;
	var scrollBound = null;

	function trigger() {
		if (fired) return;
		fired = true;
		cancelTriggers();
		openPopup();
	}

	function cancelTriggers() {
		if (timerHandle) clearTimeout(timerHandle);
		if (scrollBound)
			window.removeEventListener("scroll", scrollBound, { passive: true });
	}

	function onScroll() {
		var scrolled = window.scrollY || window.pageYOffset;
		var total =
			Math.max(
				document.body.scrollHeight,
				document.documentElement.scrollHeight,
			) - window.innerHeight;
		if (total > 0 && scrolled / total >= 0.25) trigger();
	}

	if (postedMarker) {
		/* Successful native submit — show the success card once, flag, close. */
		flag("signed_up");
		showSuccess();
		fired = true;
		openPopup();
		setTimeout(() => {
			closePopup("signed_up");
		}, 6000);
	} else if (errorsMarker) {
		/* Shopify rejected the submit (e.g. invalid email) — reopen with error. */
		fired = true;
		openPopup();
		setError(
			errorsMarker.dataset.msg
				? "Email " + errorsMarker.dataset.msg + "."
				: "Something went wrong. Please try again.",
		);
	} else {
		timerHandle = setTimeout(trigger, delay);
		scrollBound = onScroll;
		window.addEventListener("scroll", scrollBound, { passive: true });
	}

	/* ── Close on X button ────────────────────────────────────────────── */
	closeBtn.addEventListener("click", () => {
		closePopup("dismissed");
	});

	/* ── "No thanks" — second way out, always reachable on tall phones ── */
	if (dismissBtn) {
		dismissBtn.addEventListener("click", () => {
			closePopup("dismissed");
		});
	}

	/* ── Close on backdrop click (outside the card) ───────────────────── */
	dialog.addEventListener("click", (e) => {
		/* The card fills the dialog; a click on dialog itself = backdrop */
		if (e.target === dialog) closePopup("dismissed");
	});

	/* ── Close on ESC (native <dialog> handles this, but also flag it) ── */
	dialog.addEventListener("cancel", (e) => {
		e.preventDefault();
		closePopup("dismissed");
	});

	/* ── Focus trap ──────────────────────────────────────────────────────
     Native <dialog> + showModal() already traps focus in modern browsers.
     This handler adds cycling (Shift+Tab from first → last, Tab from
     last → first) for older Chromium / Safari where the native trap
     does not cycle.
  ──────────────────────────────────────────────────────────────────── */
	dialog.addEventListener("keydown", (e) => {
		if (e.key !== "Tab") return;

		var focusable = Array.from(
			dialog.querySelectorAll(
				"a[href], button:not([disabled]), input:not([disabled]), " +
					"select:not([disabled]), textarea:not([disabled]), " +
					'[tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hidden && !el.closest("[hidden]"));

		if (focusable.length === 0) return;

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (e.shiftKey) {
			if (document.activeElement === first) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}
	});

	/* ── Form validation ──────────────────────────────────────────────── */
	function validateEmail(value) {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
	}

	function setError(msg) {
		emailErr.textContent = msg;
		emailIn.setAttribute("aria-invalid", "true");
		emailIn.focus();
	}

	function clearError() {
		emailErr.textContent = "";
		emailIn.removeAttribute("aria-invalid");
	}

	/* ── Submit handler ───────────────────────────────────────────────── */
	form.addEventListener("submit", (e) => {
		clearError();

		var email = emailIn.value.trim();
		if (!email) {
			e.preventDefault();
			setError("Email is required.");
			return;
		}
		if (!validateEmail(email)) {
			e.preventDefault();
			setError("Please enter a valid email address.");
			return;
		}

		var whatnot = (document.getElementById("mccga-whatnot") || {}).value || "";

		if (KLAVIYO_KEY && KLAVIYO_LIST) {
			/* ── OPTION A: Klaviyo (AJAX) ───────────────────────────────── */
			e.preventDefault();
			var data = {
				email: email,
				first_name: (document.getElementById("mccga-fname") || {}).value || "",
				last_name: (document.getElementById("mccga-lname") || {}).value || "",
				phone: (document.getElementById("mccga-phone") || {}).value || "",
				whatnot_username: whatnot,
			};
			setLoading(true);
			submitToKlaviyo(data)
				.then(() => {
					showSuccess();
					flag("signed_up");
					setTimeout(() => {
						closePopup("signed_up");
					}, 3200);
				})
				.catch((err) => {
					setLoading(false);
					setError("Something went wrong. Please try again.");
					console.error("[mccga-popup] Submit error:", err);
				});
			return;
		}

		/* ── Fallback: NATIVE Shopify submit ────────────────────────────
		   The {% form 'customer' %} wrapper carries its own CSRF token — a
		   hand-built fetch to /contact gets HTTP 400 (verified 2026-07-06).
		   Fold the Whatnot handle into contact[tags] so it survives the
		   POST, then let the browser submit normally. Success/error state
		   is rendered by Liquid on the reload (markers read at top). */
		var tagsInput = document.getElementById("mccga-tags");
		var handle = whatnot.trim().replace(/^@/, "").replace(/,/g, " ").trim();
		if (tagsInput && handle) {
			tagsInput.value = "newsletter,vip-popup,wn:" + handle;
		}
		setLoading(true);
		/* no preventDefault — the native POST proceeds */
	});

	/* ── Loading state ────────────────────────────────────────────────── */
	function setLoading(isLoading) {
		var label = submitBtn.querySelector(".mccga-popup__cta-label");
		var spinner = submitBtn.querySelector(".mccga-popup__cta-spinner");
		submitBtn.disabled = isLoading;
		if (label) label.textContent = isLoading ? "Joining…" : "Get Early Access";
		if (spinner) spinner.hidden = !isLoading;
	}

	/* ── Success state ────────────────────────────────────────────────── */
	function showSuccess() {
		form.hidden = true;
		successEl.hidden = false;
		submitBtn.disabled = false;
		successEl.focus();
	}

	/* ── OPTION A: Klaviyo client-side subscribe ──────────────────────── */
	function submitToKlaviyo(data) {
		/* Klaviyo Profiles API v2 — Subscribe endpoint (2024-06-15 revision)  */
		/* Public key only; safe for browser use. No secret key needed.        */
		var profile = {
			type: "profile",
			attributes: {
				email: data.email,
			},
		};
		if (data.first_name) profile.attributes.first_name = data.first_name;
		if (data.last_name) profile.attributes.last_name = data.last_name;
		if (data.phone) profile.attributes.phone_number = data.phone;
		if (data.whatnot_username) {
			profile.attributes.properties = {
				whatnot_username: data.whatnot_username,
			};
		}

		var body = JSON.stringify({
			data: {
				type: "subscription",
				attributes: {
					profile: { data: profile },
				},
				relationships: {
					list: {
						data: { type: "list", id: KLAVIYO_LIST },
					},
				},
			},
		});

		return fetch(
			"https://a.klaviyo.com/client/subscriptions/?company_id=" + KLAVIYO_KEY,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					revision: "2024-06-15",
				},
				body: body,
			},
		).then((res) => {
			/* Klaviyo returns 202 Accepted on success */
			if (!res.ok && res.status !== 202) {
				return res.text().then((t) => {
					throw new Error("Klaviyo " + res.status + ": " + t);
				});
			}
		});
	}

	/* ── OPTION C (fallback): native {% form 'customer' %} submit ───────
	   No function needed — the submit handler lets the browser POST the
	   Liquid-rendered form, which carries Shopify's CSRF token. The old
	   fetch("/contact") implementation was removed 2026-07-06 after Shopify
	   started rejecting tokenless posts with HTTP 400. */
})();
